<template>
    <div class="e-steps-wrapper" :class="{ 'tw:flex tw:flex-col': vertical }">
        <!-- Steps Indicators -->
        <div
            class="e-steps-indicators"
            :class="{
                'tw:flex tw:flex-col tw:gap-8': vertical,
                'tw:flex tw:justify-between tw:mb-4': !vertical
            }"
        >
            <div
                v-for="(step, index) in visibleSteps"
                :key="step.uid"
                class="e-step-indicator tw:flex tw:items-center tw:relative"
                :class="{
                    'tw:cursor-pointer': step.clickable && !step.disabled,
                    'tw:opacity-50 tw:cursor-not-allowed': step.disabled,
                    'is-active': modelValue === index
                }"
                @click="handleStepClick(index, step)"
            >
                <div
                    v-if="vertical && index < visibleSteps.length - 1"
                    class="tw:absolute tw:top-8 tw:bottom-[-2rem] tw:w-1 tw:bg-border"
                    :class="[{ 'tw:bg-primary': index < modelValue }, verticalLinePosition]"
                />

                <div
                    v-if="!vertical && index < visibleSteps.length - 1"
                    class="tw:absolute tw:right-[-50%] tw:top-1/2 tw:-mt-0.5 tw:h-1 tw:bg-border tw:z-0"
                    :class="[{ 'tw:bg-primary': index < modelValue }, horizontalLinePosition]"
                />

                <!-- Icon/Marker -->
                <div
                    class="e-step-marker tw:z-10 tw:flex tw:items-center tw:justify-center tw:rounded-full tw:transition-colors tw:duration-300"
                    :class="[
                        sizeClasses,
                        {
                            'tw:bg-primary tw:text-white': index < modelValue,
                            'tw:bg-white tw:border-2 tw:border-primary tw:text-primary': modelValue === index,
                            'tw:bg-white tw:border-2 tw:border-border tw:text-grey-light': index > modelValue
                        }
                    ]"
                >
                    <e-icon v-if="step.icon" :icon="step.icon" :size="iconSize" />
                    <span v-else>{{ index + 1 }}</span>
                </div>

                <!-- Label -->
                <div
                    class="e-step-label tw:ml-3"
                    :class="{
                        'tw:font-bold tw:text-black': modelValue === index,
                        'tw:text-grey': modelValue !== index
                    }"
                >
                    {{ step.label }}
                </div>
            </div>
        </div>

        <!-- Step Content -->
        <div class="e-step-content tw:mt-4">
            <slot />
        </div>
    </div>
</template>

<script>
import { computed, provide, ref } from 'vue';

export default {
    name: 'ESteps',
    inheritAttrs: false,
    props: {
        modelValue: {
            type: Number,
            default: 0,
        },
        vertical: {
            type: Boolean,
            default: false,
        },
        labelPosition: {
            type: String,
            default: 'bottom',
        },
        size: {
            type: String,
            default: 'is-medium',
        },
        type: {
            type: String,
            default: 'is-primary',
        },
        animated: {
            type: Boolean,
            default: true,
        },
        hasNavigation: {
            type: Boolean,
            default: true,
        },
    },
    emits: ['update:modelValue'],
    setup(props, { emit }) {
        const steps = ref([]);

        const registerStep = step => {
            steps.value.push(step);
        };

        const unregisterStep = step => {
            const index = steps.value.indexOf(step);
            if (index > -1) {
                steps.value.splice(index, 1);
            }
        };

        provide('registerStep', registerStep);
        provide('unregisterStep', unregisterStep);
        provide('steps', steps);

        // Provide active state to children
        provide(
            'activeStepIndex',
            computed(() => props.modelValue)
        );

        const visibleSteps = computed(() => steps.value.filter(step => step.visible));

        const handleStepClick = (index, step) => {
            if (step.clickable && !step.disabled) {
                emit('update:modelValue', index);
            }
        };

        const sizeClasses = computed(() => {
            switch (props.size) {
                case 'is-small':
                    return 'tw:w-8 tw:h-8 tw:text-sm';
                case 'is-medium':
                    return 'tw:w-10 tw:h-10 tw:text-base';
                case 'is-large':
                    return 'tw:w-12 tw:h-12 tw:text-lg';
                default:
                    return 'tw:w-10 tw:h-10 tw:text-base';
            }
        });

        const iconSize = computed(() => {
            switch (props.size) {
                case 'is-small':
                    return 'is-small';
                case 'is-medium':
                    return 'is-medium';
                case 'is-large':
                    return 'is-large';
                default:
                    return 'is-medium';
            }
        });

        const verticalLinePosition = computed(() => {
            switch (props.size) {
                case 'is-small':
                    return 'tw:left-[0.875rem]';
                case 'is-medium':
                    return 'tw:left-[1.125rem]';
                case 'is-large':
                    return 'tw:left-[1.375rem]';
                default:
                    return 'tw:left-[1.125rem]';
            }
        });

        const horizontalLinePosition = computed(() => {
            switch (props.size) {
                case 'is-small':
                    return 'tw:left-8';
                case 'is-medium':
                    return 'tw:left-10';
                case 'is-large':
                    return 'tw:left-12';
                default:
                    return 'tw:left-10';
            }
        });

        return {
            steps,
            visibleSteps,
            handleStepClick,
            sizeClasses,
            iconSize,
            verticalLinePosition,
            horizontalLinePosition,
        };
    },
};
</script>
